import { X, Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRef, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = (text || "").split(" ");
  let line = "";
  let lines = 0;
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxWidth && line !== "") {
      ctx.fillText(line.trim(), x, y);
      line = w + " ";
      y += lineHeight;
      lines += 1;
      if (maxLines && lines >= maxLines - 1) break;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, y);
  return y + lineHeight;
}

function drawWinCard(ctx, W, H, recap, name) {
  ctx.fillStyle = "#0B0C0E";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#FD0F10";
  ctx.fillRect(0, 0, W, 8);
  for (let i = 0; i < 500; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }
  ctx.textBaseline = "top";
  // Tag
  ctx.fillStyle = "#FD0F10";
  ctx.font = "700 26px 'JetBrains Mono', monospace";
  ctx.fillText("WIN LOGGED", 80, 110);
  // Big checkmark circle
  ctx.strokeStyle = "#FD0F10";
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.arc(160, 300, 70, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.lineWidth = 16;
  ctx.moveTo(128, 300);
  ctx.lineTo(152, 326);
  ctx.lineTo(200, 268);
  ctx.stroke();
  // Title
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 66px Outfit, sans-serif";
  const afterTitle = wrapText(ctx, (recap.title || "A step done").toUpperCase(), 80, 420, W - 160, 74, 3);
  // Next step label
  ctx.fillStyle = "#FD0F10";
  ctx.font = "700 22px 'JetBrains Mono', monospace";
  ctx.fillText("I DID THIS", 80, afterTitle + 20);
  // Next step body
  ctx.fillStyle = "#E5E7EB";
  ctx.font = "500 34px 'DM Sans', sans-serif";
  wrapText(ctx, recap.next_step || "", 80, afterTitle + 64, W - 160, 46, 5);
  // Brand footer
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 34px Outfit, sans-serif";
  ctx.fillText("I HATE ", 80, H - 140);
  const w1 = ctx.measureText("I HATE ").width;
  ctx.fillStyle = "#FD0F10";
  ctx.fillText("MY JOB.", 80 + w1, H - 140);
  ctx.fillStyle = "#6B7280";
  ctx.font = "400 24px 'DM Sans', sans-serif";
  ctx.fillText(name ? `${name} is building the way out.` : "Building the way out, one step at a time.", 80, H - 88);
}

export default function WinCardModal({ recap, onClose }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [hostedUrl, setHostedUrl] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => { setHostedUrl(null); }, [recap?.id]);

  useEffect(() => {
    if (!recap) return;
    const draw = () => {
      const cvs = canvasRef.current;
      if (!cvs) return;
      const ctx = cvs.getContext("2d");
      drawWinCard(ctx, cvs.width, cvs.height, recap, user?.name);
      setDataUrl(cvs.toDataURL("image/png"));
    };
    draw();
    const t = setTimeout(draw, 400);
    return () => clearTimeout(t);
  }, [recap, user?.name]);

  if (!recap) return null;

  const ensureHosted = async () => {
    if (hostedUrl) return hostedUrl;
    if (!dataUrl) return null;
    const { data } = await api.post("/win-card", { image_base64: dataUrl, recap_id: recap.id });
    const url = `${window.location.origin}${data.path}`;
    setHostedUrl(url);
    return url;
  };

  const share = async () => {
    setSharing(true);
    try {
      const link = await ensureHosted();
      const text = `Win: ${recap.next_step}`;
      const url = link || window.location.origin;
      if (navigator.share) {
        try { await navigator.share({ title: "My win", text, url }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied. Paste it anywhere.");
    } catch {
      toast.error("Couldn't create a share link. Try Download instead.");
    } finally { setSharing(false); }
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "i-hate-my-job-win.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Saved to your device.");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-5" data-testid="win-card-modal">
      <div className="max-w-md w-full card-panel-elevated p-6 relative border-[#FD0F10]">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close" data-testid="win-card-close">
          <X size={20} />
        </button>
        <div className="eyebrow text-[#FD0F10]">SHARE YOUR WIN</div>
        <h2 className="font-display text-2xl font-black mt-2 text-white">{recap.title}</h2>
        {dataUrl && (
          <div className="mt-4 rounded-lg overflow-hidden border border-[#2A2E39]" data-testid="win-card-preview">
            <img src={dataUrl} alt="Win card" className="w-full block" />
          </div>
        )}
        <canvas ref={canvasRef} width={1080} height={1080} className="hidden" />
        <div className="mt-5 flex gap-2 flex-wrap">
          <button onClick={share} disabled={sharing} className="flex-1 hero-accent text-white font-bold py-3 rounded flex items-center justify-center gap-2 disabled:opacity-60" data-testid="win-card-share">
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Share
          </button>
          <button onClick={download} className="flex-1 border border-[#FD0F10] text-[#FD0F10] font-bold py-3 rounded hover:bg-[#FD0F10] hover:text-white flex items-center justify-center gap-2" data-testid="win-card-download">
            <Download size={16} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
